/* MARCA | Minimum Order Quantity System | 2026-01-30 */
/* 
 * MARCA | Parses product tags (MIN-ORDER:X, MIN-ORDER-STEP:Y) and enforces minimum quantities | 2026-01-30
 * MARCA | Applies to product page quantity selectors, add-to-cart buttons, and cart drawer | 2026-01-30
 */

/**
 * Extract minimum order configuration from product tags
 * @param {Array} tags - Product tags array
 * @returns {Object} - { minQuantity: number, stepQuantity: number }
 */
function parseMinOrderTags(tags) {
  let minQuantity = 1;
  let stepQuantity = 1;
  
  if (!tags || !Array.isArray(tags)) {
    return { minQuantity, stepQuantity };
  }
  
  tags.forEach(tag => {
    const tagUpper = tag.toUpperCase();
    
    // Parse MIN-ORDER:X tag
    if (tagUpper.startsWith('MIN-ORDER:')) {
      const value = parseInt(tagUpper.replace('MIN-ORDER:', ''), 10);
      if (!isNaN(value) && value > 0) {
        minQuantity = value;
      }
    }
    
    // Parse MIN-ORDER-STEP:X tag
    if (tagUpper.startsWith('MIN-ORDER-STEP:')) {
      const value = parseInt(tagUpper.replace('MIN-ORDER-STEP:', ''), 10);
      if (!isNaN(value) && value > 0) {
        stepQuantity = value;
      }
    }
  });
  
  return { minQuantity, stepQuantity };
}

/**
 * Small utilities for consistent numeric coercion + event dispatching
 * MARCA | Refactor: avoid repeated parseInt / event boilerplate | 2026-01-30
 */
function toInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function getInputQuantity(input, fallback) {
  return toInt(input && input.value, fallback);
}

function getDatasetInt(el, key, fallback) {
  return toInt(el && el.dataset ? el.dataset[key] : undefined, fallback);
}

function setStepAttribute(input, stepQuantity) {
  if (!input) return;
  // MARCA | Only set step when > 1 (avoids other scripts treating step="1" as a string) | 2026-01-30
  if (stepQuantity && stepQuantity > 1) {
    input.setAttribute('step', String(stepQuantity));
  } else {
    input.removeAttribute('step');
  }
}

function setQuantityAndNotify(input, nextQuantity, { notifyInput = false, notifyChange = true } = {}) {
  if (!input) return;
  input.value = String(nextQuantity);
  if (notifyInput) input.dispatchEvent(new Event('input', { bubbles: true }));
  if (notifyChange) input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Apply minimum order constraints to a quantity input
 * @param {HTMLInputElement} input - The quantity input element
 * @param {number} minQuantity - Minimum allowed quantity
 * @param {number} stepQuantity - Step increment
 */
function applyMinOrderConstraints(input, minQuantity, stepQuantity) {
  if (!input) return;
  
  input.setAttribute('min', String(minQuantity));
  setStepAttribute(input, stepQuantity);
  input.dataset.minOrder = String(minQuantity);
  input.dataset.minOrderStep = String(stepQuantity);
  
  // Set initial value to minimum if below minimum
  const currentValue = getInputQuantity(input, 1);
  if (currentValue < minQuantity) {
    setQuantityAndNotify(input, minQuantity, { notifyInput: false, notifyChange: false });
  }
}

/**
 * Initialize minimum order quantity for product page
 * MARCA | Sets up quantity selector and add-to-cart button behavior | 2026-01-30
 */
function initProductPageMinOrder() {
  // Find product form and get product data
  const productForm = document.querySelector('product-form');
  if (!productForm) return;
  
  // Get product data from JSON script tag
  let productData = null;
  const productJsonScript = document.getElementById('product-tags-data');
  
  if (productJsonScript) {
    try {
      productData = JSON.parse(productJsonScript.textContent);
    } catch (e) {
      console.warn('Could not parse product tags data', e);
    }
  }
  
  if (!productData || !productData.tags) return;
  
  const { minQuantity, stepQuantity } = parseMinOrderTags(productData.tags);
  
  // Only proceed if there's a minimum order requirement
  if (minQuantity <= 1) return;
  
  // MARCA | Add visual notice about minimum order requirement | 2026-01-30
  const addMinOrderNotice = () => {
    const buyButtonsContainer = productForm.querySelector('buy-buttons');
    if (!buyButtonsContainer) return;
    
    // Check if notice already exists
    if (buyButtonsContainer.querySelector('.min-order-notice')) return;
    
    const notice = document.createElement('div');
    notice.className = 'min-order-notice';
    notice.style.cssText = 'margin-bottom: 0.25rem; padding: 0.75rem 1rem; background-color: #f3f4f6; border-left: 3px solid #8b7353; font-size: 0.875rem; color: #1f2937;';
    
    let noticeText = `<strong>Minimum order:</strong> ${minQuantity} units`;
    if (stepQuantity > 1) {
      noticeText += ` (in increments of ${stepQuantity})`;
    }
    notice.innerHTML = noticeText;
    
    // Insert before the quantity/submit row
    const quantityRow = buyButtonsContainer.querySelector('.quantity-submit-row');
    if (quantityRow) {
      quantityRow.parentNode.insertBefore(notice, quantityRow);
    } else {
      buyButtonsContainer.insertBefore(notice, buyButtonsContainer.firstChild);
    }
  };
  
  addMinOrderNotice();
  
  // MARCA | Ensure quantity input exists, create if missing | 2026-01-30
  let quantityInput = productForm.querySelector('input[name="quantity"]');
  
  // If no quantity input exists, create one
  if (!quantityInput) {
    const form = productForm.querySelector('form');
    const addToCartBtn = form ? form.querySelector('button[name="add"]') : null;
    
    if (form && addToCartBtn) {
      // Create quantity wrapper
      const quantityWrapper = document.createElement('quantity-wrapper');
      quantityWrapper.className = 'buttoned-input';
      quantityWrapper.style.cssText = 'margin-bottom: 1rem;';
      
      // Create decrease button
      const decreaseBtn = document.createElement('a');
      decreaseBtn.href = '#';
      decreaseBtn.setAttribute('data-quantity', 'down');
      decreaseBtn.setAttribute('aria-label', 'Decrease quantity');
      decreaseBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2"><rect width="12" height="2"/></svg>';
      
      // Create input
      quantityInput = document.createElement('input');
      quantityInput.type = 'number';
      quantityInput.name = 'quantity';
      quantityInput.id = 'quantity';
      quantityInput.value = '1';
      quantityInput.setAttribute('aria-label', 'Quantity');
      
      // Create increase button
      const increaseBtn = document.createElement('a');
      increaseBtn.href = '#';
      increaseBtn.setAttribute('data-quantity', 'up');
      increaseBtn.setAttribute('aria-label', 'Increase quantity');
      increaseBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path d="M5,0h2v12H5V0z M0,5h12v2H0V5z"/></svg>';
      
      // Create label
      const label = document.createElement('label');
      label.className = 'label';
      label.htmlFor = 'quantity';
      label.textContent = 'Quantity';
      label.style.display = 'block';
      label.style.marginBottom = '0.5rem';
      
      // Assemble elements
      quantityWrapper.appendChild(decreaseBtn);
      quantityWrapper.appendChild(quantityInput);
      quantityWrapper.appendChild(increaseBtn);
      
      // Insert before add to cart button
      const submitRow = addToCartBtn.closest('.quantity-submit-row__submit');
      if (submitRow) {
        const container = submitRow.parentElement;
        const labelWrapper = document.createElement('div');
        labelWrapper.appendChild(label);
        labelWrapper.appendChild(quantityWrapper);
        container.insertBefore(labelWrapper, submitRow);
      } else {
        addToCartBtn.parentElement.insertBefore(label, addToCartBtn);
        addToCartBtn.parentElement.insertBefore(quantityWrapper, addToCartBtn);
      }
    }
  }
  
  // Apply constraints to quantity input
  if (quantityInput) {
    applyMinOrderConstraints(quantityInput, minQuantity, stepQuantity);
    
    // MARCA | Update decrease button state based on current value | 2026-01-30
    function updateDecreaseButtonState() {
      const currentValue = getInputQuantity(quantityInput, minQuantity);
      const quantityWrapper = quantityInput.closest('quantity-wrapper');
      if (quantityWrapper) {
        const decreaseBtn = quantityWrapper.querySelector('[data-quantity="down"]');
        if (decreaseBtn) {
          if (currentValue <= minQuantity) {
            decreaseBtn.classList.add('disabled');
            decreaseBtn.style.opacity = '0.5';
            decreaseBtn.style.cursor = 'not-allowed';
          } else {
            decreaseBtn.classList.remove('disabled');
            decreaseBtn.style.opacity = '';
            decreaseBtn.style.cursor = '';
          }
        }
      }
    }
    
    // Initial state update
    updateDecreaseButtonState();
    
    // MARCA | Enforce minimum when using quantity buttons | 2026-01-30
    const quantityWrapper = quantityInput.closest('quantity-wrapper');
    if (quantityWrapper) {
      const decreaseBtn = quantityWrapper.querySelector('[data-quantity="down"]');
      if (decreaseBtn) {
        // MARCA | Override decrease click to ensure numeric math (prevents "20" + 1 => "201" style bugs) | 2026-01-30
        decreaseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const safeCurrent = getInputQuantity(quantityInput, minQuantity);

          const newValue = safeCurrent - stepQuantity;
          setQuantityAndNotify(
            quantityInput,
            (newValue < minQuantity) ? minQuantity : newValue,
            { notifyInput: true, notifyChange: true }
          );
          updateDecreaseButtonState();

          return false;
        }, true);
      }
      
      // MARCA | Handle increase button with step | 2026-01-30
      const increaseBtn = quantityWrapper.querySelector('[data-quantity="up"]');
      if (increaseBtn) {
        // MARCA | Override increase click to ensure numeric math (fixes "20" + 1 => "201") | 2026-01-30
        increaseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const safeCurrent = getInputQuantity(quantityInput, minQuantity);

          const newValue = Math.max(minQuantity, safeCurrent + stepQuantity);
          setQuantityAndNotify(quantityInput, newValue, { notifyInput: true, notifyChange: true });
          updateDecreaseButtonState();

          return false;
        }, true);
      }
    }
    
    // MARCA | Update button state when input changes | 2026-01-30
    quantityInput.addEventListener('input', updateDecreaseButtonState);
    quantityInput.addEventListener('change', updateDecreaseButtonState);
  }
  
  // MARCA | Override form submission to ensure minimum quantity | 2026-01-30
  const form = productForm.querySelector('form');
  if (form) {
    form.addEventListener('submit', function(e) {
      const qtyInput = this.querySelector('input[name="quantity"]');
      if (qtyInput) {
        const currentQty = parseInt(qtyInput.value, 10) || 1;
        if (currentQty < minQuantity) {
          qtyInput.value = minQuantity;
        }
        
        // MARCA | Ensure quantity aligns with step | 2026-01-30
        if (stepQuantity > 1) {
          const adjustedQty = parseInt(qtyInput.value, 10);
          const remainder = (adjustedQty - minQuantity) % stepQuantity;
          if (remainder !== 0) {
            qtyInput.value = adjustedQty - remainder;
          }
        }
      }
    }, true); // Use capture phase to run before other handlers
  }
  
  // MARCA | Also handle add-to-cart button clicks directly | 2026-01-30
  const addToCartBtn = productForm.querySelector('button[name="add"], .add-to-cart');
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', function() {
      const qtyInput = productForm.querySelector('input[name="quantity"]');
      if (qtyInput) {
        const currentQty = parseInt(qtyInput.value, 10) || 1;
        if (currentQty < minQuantity) {
          qtyInput.value = minQuantity;
        }
      }
    }, true);
  }
}

/**
 * Initialize minimum order quantity for cart drawer/page
 * MARCA | Enforces minimum quantities in cart and validates before checkout | 2026-01-30
 */
function initCartMinOrder() {
  const cartForm = document.querySelector('cart-form');
  if (!cartForm) return;
  
  // MARCA | Process each cart item to apply minimum constraints | 2026-01-30
  const cartItems = cartForm.querySelectorAll('.cart-item');
  cartItems.forEach(item => {
    const quantityInput = item.querySelector('.cart-item__quantity-input');
    if (!quantityInput) return;
    
    // MARCA | Get product tags from data attribute | 2026-01-30
    const productHandle = item.dataset.productHandle;
    const productTagsStr = item.dataset.productTags || '';
    const productTags = productTagsStr ? productTagsStr.split(',') : [];
    
    if (!productTags.length) return;
    
    // Store product handle on input for later validation
    quantityInput.dataset.productHandle = productHandle;
    
    const { minQuantity, stepQuantity } = parseMinOrderTags(productTags);
    
    if (minQuantity <= 1) return;
    
    // Store min order data on input
    quantityInput.dataset.minOrder = minQuantity;
    quantityInput.dataset.minOrderStep = stepQuantity;
    
    // Apply constraints
    applyMinOrderConstraints(quantityInput, minQuantity, stepQuantity);
    
    // MARCA | Add visual notice to cart item about minimum order | 2026-01-30
    const addCartMinOrderNotice = () => {
      const quantityColumn = item.querySelector('.cart-item__quantity');
      if (!quantityColumn) return;
      
      // Check if notice already exists
      if (quantityColumn.querySelector('.cart-min-order-notice')) return;
      
      const notice = document.createElement('div');
      notice.className = 'cart-min-order-notice small-text';
      notice.style.cssText = 'margin-top: 0.25rem; color: #6b7280; font-size: 0.75rem;';
      
      let noticeText = `Minimum Quantity: ${minQuantity}`;
      if (stepQuantity > 1) {
        noticeText += `, Step: ${stepQuantity}`;
      }
      notice.textContent = noticeText;
      
      quantityColumn.appendChild(notice);
    };
    
    addCartMinOrderNotice();
    
    // MARCA | Add remove button for minimum order products | 2026-01-30
    const addMinOrderRemoveButton = () => {
      const quantityColumn = item.querySelector('.cart-item__quantity');
      if (!quantityColumn) return;
      
      // Check if button already exists
      if (quantityColumn.querySelector('.min-order-remove-btn')) return;
      
      const quantityWrapper = quantityColumn.querySelector('.quantity');
      if (!quantityWrapper) return;
      
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'min-order-remove-btn small-text';
      removeBtn.style.cssText = 'display: none; background: none; border: none; padding: 0; cursor: pointer; color: #000; text-decoration: underline; font-size: 0.875rem;';
      removeBtn.textContent = 'REMOVE';
      removeBtn.dataset.itemKey = quantityInput.dataset.key;
      
      // Handle remove button click - no confirmation
      removeBtn.addEventListener('click', function(e) {
        e.preventDefault();
        // Set quantity to 0 to remove item
        quantityInput.value = 0;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      // Insert before the quantity wrapper
      quantityWrapper.parentNode.insertBefore(removeBtn, quantityWrapper);
    };
    
    addMinOrderRemoveButton();
    
    // MARCA | Update cart item button state based on current value | 2026-01-30
    function updateCartButtonState() {
      const currentValue = getInputQuantity(quantityInput, minQuantity);
      const decreaseBtn = item.querySelector('.quantity-down');
      
      if (decreaseBtn) {
        if (currentValue <= minQuantity) {
          // Disable decrease button (keep visible, just disabled)
          decreaseBtn.classList.add('disabled');
          decreaseBtn.style.opacity = '0.5';
          decreaseBtn.style.cursor = 'not-allowed';

        } else {
          // Enable decrease button
          decreaseBtn.classList.remove('disabled');
          decreaseBtn.style.opacity = '';
          decreaseBtn.style.cursor = '';
        }
      }
    }
    
    // MARCA | Always show REMOVE button for products with minimum order | 2026-01-30
    const minOrderRemoveBtn = item.querySelector('.min-order-remove-btn');
    const standardRemoveLink = item.querySelector('.cart-item__remove');
    
    // Show REMOVE button and hide standard link for all minimum order products
    if (minOrderRemoveBtn) {
      minOrderRemoveBtn.style.display = 'inline';
    }
    if (standardRemoveLink) {
      standardRemoveLink.style.display = 'none';
    }
    
    // Initial state update
    updateCartButtonState();
    
    // MARCA | Prevent decreasing below minimum | 2026-01-30
    const decreaseBtn = item.querySelector('.quantity-down');
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', function(e) {
        const currentValue = getInputQuantity(quantityInput, minQuantity);
        const newValue = currentValue - stepQuantity;
        if (newValue < minQuantity) {
          e.preventDefault();
          e.stopPropagation();
          setQuantityAndNotify(quantityInput, minQuantity, { notifyInput: false, notifyChange: false });
          updateCartButtonState();
          return false;
        }
      }, true);
    }
    
    // MARCA | Enforce step on increase button | 2026-01-30
    const increaseBtn = item.querySelector('.quantity-up');
    if (increaseBtn) {
      increaseBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const safeCurrent = getInputQuantity(quantityInput, minQuantity);
        setQuantityAndNotify(quantityInput, safeCurrent + stepQuantity, { notifyInput: false, notifyChange: true });
        updateCartButtonState();
        return false;
      }, true);
    }
    
    // MARCA | Validate input changes | 2026-01-30
    quantityInput.addEventListener('change', function() {
      const min = getDatasetInt(this, 'minOrder', 1);
      const currentValue = getInputQuantity(this, 0);
      
      if (currentValue > 0 && currentValue < min) {
        setQuantityAndNotify(this, min, { notifyInput: false, notifyChange: false });
      }
      updateCartButtonState();
    });
    
    // MARCA | Enforce step increments on blur | 2026-01-30
    quantityInput.addEventListener('blur', function() {
      const min = getDatasetInt(this, 'minOrder', 1);
      const step = getDatasetInt(this, 'minOrderStep', 1);
      const currentValue = getInputQuantity(this, 0);
      
      if (currentValue > 0 && currentValue < min) {
        setQuantityAndNotify(this, min, { notifyInput: false, notifyChange: false });
      } else if (currentValue >= min && step > 1) {
        // Round to nearest valid step value
        const remainder = (currentValue - min) % step;
        if (remainder !== 0) {
          const roundedDown = currentValue - remainder;
          const roundedUp = roundedDown + step;
          // Round to nearest
          setQuantityAndNotify(this, (remainder >= step / 2) ? roundedUp : roundedDown, { notifyInput: false, notifyChange: false });
        }
      }
      updateCartButtonState();
    });
    
    // MARCA | Update button state on input | 2026-01-30
    quantityInput.addEventListener('input', updateCartButtonState);
  });
}

/**
 * Validate cart before checkout
 * MARCA | Prevents checkout if minimum order quantities not met | 2026-01-30
 */
function validateCartBeforeCheckout() {
  const checkoutButtons = document.querySelectorAll('a[href*="/checkout"], button[name="checkout"], .js-checkout-btn');
  
  checkoutButtons.forEach(button => {
    button.addEventListener('click', function(e) {
      const cartForm = document.querySelector('cart-form');
      if (!cartForm) return;
      
      const violations = [];
      
      // MARCA | Check each cart item for minimum quantity violations | 2026-01-30
      const cartItems = cartForm.querySelectorAll('.cart-item');
      cartItems.forEach(item => {
        const quantityInput = item.querySelector('.cart-item__quantity-input');
        if (!quantityInput) return;
        
        const minOrder = parseInt(quantityInput.dataset.minOrder, 10);
        if (!minOrder || minOrder <= 1) return;
        
        const currentQty = parseInt(quantityInput.value, 10) || 0;
        
        // MARCA | Check if quantity meets minimum requirement | 2026-01-30
        if (currentQty > 0 && currentQty < minOrder) {
          // Get product title from cart item
          const titleElement = item.querySelector('.cart-item__title a');
          const productTitle = titleElement ? titleElement.textContent.trim() : (quantityInput.dataset.productHandle || 'Unknown product').replace(/-/g, ' ');
          
          violations.push({
            title: productTitle,
            currentQty: currentQty,
            minQty: minOrder
          });
        }
      });
      
      if (violations.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // MARCA | Build error message with all violations | 2026-01-30
        let message = 'Cannot proceed to checkout. Please update the following items:\n\n';
        violations.forEach(v => {
          message += `• ${v.title}: Currently ${v.currentQty}, minimum required is ${v.minQty}\n`;
        });
        
        alert(message);
        
        return false;
      }
    }, true); // Use capture phase to intercept before other handlers
  });
}

/**
 * Initialize all minimum order quantity features
 * MARCA | Main initialization function called on page load | 2026-01-30
 */
function initMinOrderQuantity() {
  // Initialize product page
  if (document.querySelector('product-form')) {
    initProductPageMinOrder();
  }
  
  // Initialize cart
  if (document.querySelector('cart-form')) {
    initCartMinOrder();
    validateCartBeforeCheckout();
  }
  
  // MARCA | Re-initialize cart when drawer is refreshed | 2026-01-30
  document.addEventListener('on:cart:after-merge', () => {
    setTimeout(() => {
      initCartMinOrder();
      validateCartBeforeCheckout();
    }, 100);
  });
}

// MARCA | Initialize on DOM ready and expose globally | 2026-01-30
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMinOrderQuantity);
} else {
  initMinOrderQuantity();
}

// Expose for manual initialization if needed
window.initMinOrderQuantity = initMinOrderQuantity;
